import { useCallback } from "react"
import {
  AlertCircle,
  Block,
  Button,
  FlexGrid,
  FlexGridItem,
  HeadingXXLarge,
  LabelMedium,
  ParagraphMedium,
  ParagraphXSmall,
  Tag02,
  useStyletron,
} from "@mezo-org/mezo-clay"

import marketModalItemBackground from "#/assets/market-item-modal-background.png"
import {
  resetCurrentProduct,
  selectCurrentProduct,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { DEFAULT_MODAL_Z_INDEX } from "#/shared/constants"
import { useMarketItemModalButton, useOrderProduct } from "#/shared/hooks"

import { MatsnetActionButton } from "../Button"
import { BaseModal, BaseModalProps } from "../Modal"

export default function MarketItemModal({ isOpen, onClose }: BaseModalProps) {
  const currentProduct = useDappSelector(selectCurrentProduct)

  const [css, theme] = useStyletron()
  const dispatch = useDappDispatch()

  const { orderItem, isLoading } = useOrderProduct()

  const { marketItemModalButtonLabel, isButtonDisabled } =
    useMarketItemModalButton()

  const closeModalHandler = useCallback(() => {
    onClose?.({})
    dispatch(resetCurrentProduct())
  }, [onClose, dispatch])

  if (!currentProduct) return null

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={closeModalHandler}
      closeable
      overrides={{
        Root: { style: () => ({ zIndex: DEFAULT_MODAL_Z_INDEX }) },
        Dialog: {
          style: () => ({
            width: "100%",
            height: "100vh",
            margin: 0,
            borderRadius: 0,
          }),
        },
      }}
    >
      <FlexGrid
        flexGridColumnCount={2}
        height="100%"
        backgroundColor={theme.colors.white}
      >
        <FlexGridItem position="relative" overflow="hidden">
          <Block
            as="img"
            src={marketModalItemBackground}
            height="100%"
            width="100%"
            className={css({ objectFit: "cover" })}
          />
          <Block
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            position="absolute"
            top="50%"
            left="50%"
            padding={theme.sizing.scale1000}
            className={css({ transform: "translate(-50%, -50%)" })}
          >
            <Block
              as="img"
              src={currentProduct.fullImage}
              height="100%"
              width="100%"
              className={css({ objectFit: "contain" })}
            />
          </Block>
        </FlexGridItem>
        <FlexGridItem padding="92px 176px 40px 74px">
          <Block
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
          >
            <Block>
              <Block paddingRight={theme.sizing.scale800}>
                <HeadingXXLarge marginBottom={theme.sizing.scale600}>
                  {currentProduct.name}
                </HeadingXXLarge>
                <ParagraphMedium
                  color={theme.colors.contentTertiary}
                  marginBottom={theme.sizing.scale800}
                >
                  {currentProduct.description}
                </ParagraphMedium>
                <Button
                  $as="div"
                  kind="secondary"
                  size="medium"
                  shape="pill"
                  startEnhancer={
                    <Tag02 size={16} color={theme.colors.contentPrimary} />
                  }
                  overrides={{
                    BaseButton: { style: () => ({ pointerEvents: "none" }) },
                  }}
                >
                  <LabelMedium>{currentProduct.price} MUSD</LabelMedium>
                </Button>
              </Block>
              <MatsnetActionButton
                onClick={orderItem}
                isDisabled={isButtonDisabled}
                isLoading={isLoading}
                size="large"
                shape="pill"
                baseButtonOverrides={{
                  marginTop: "80px",
                }}
              >
                {marketItemModalButtonLabel}
              </MatsnetActionButton>
              <Block
                marginTop={theme.sizing.scale700}
                paddingRight={theme.sizing.scale800}
              >
                <ParagraphMedium color={theme.colors.contentTertiary}>
                  We will keep a record of your choice and we might reach out
                  later through Mezo notifications to get you your collected
                  item.
                </ParagraphMedium>
              </Block>
            </Block>
            <ParagraphXSmall
              color={theme.colors.contentTertiary}
              display="flex"
              alignItems="center"
              className={css({ gap: theme.sizing.scale100 })}
            >
              <AlertCircle color={theme.colors.gray400} size={14} />
              Image is a placeholder, final product may differ
            </ParagraphXSmall>
          </Block>
        </FlexGridItem>
      </FlexGrid>
    </BaseModal>
  )
}
